import { useSelector } from "react-redux";
import useDataSuite from "../useDataSuite";
import { Col, Row, Spinner } from "reactstrap";
import { Table, Column, AutoSizer } from "react-virtualized";
import { Doughnut, Bar } from "react-chartjs-2";
import { BsCircleFill } from "react-icons/bs";
import { FaCircle } from "react-icons/fa";
import { deprioritizedBrands, tableColors, Theme } from "../../../config/constants";
import formatter from "../../../config/formatters";
import CollapsibleDataTable from "../../../sharedComponents/Tables/CollapsibleDataTable";
import EmptySearchResultsMessage from "../../../sharedComponents/ErrorMessages/EmptySearchResultsMessage";
import partition from "../../../sharedComponents/Helper/arrayPartitioner";

const { numberFormatter, percentFormatter } = formatter();

const useCompetitiveInsightsWidget = () => {
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.competitiveInsightsLoading);
    const competitiveInsightsTableData = useSelector(({datasuite}) => datasuite.widgetData.competitiveInsightsData);
    const competitiveInsightsDetailsTableData = useSelector(({datasuite}) => datasuite.widgetDetailsData.competitiveInsightsData);
    var competitiveInsightsRightTableData = [];
    const widgetTitle = "Competitive Insights";
    var competitiveInsightsTableLabels = competitiveInsightsTableData?.competitors?.slice(0,5).map((value, idx) => {
        return "Competitor " + (idx + 1);
    });
    var competitiveInsightsDetailsTableLabels = competitiveInsightsDetailsTableData?.competitors?.slice(0,5).map((value, idx) => {
        return "Competitor " + (idx + 1);
    });
    competitiveInsightsTableLabels?.unshift('Manufacturer');
    competitiveInsightsDetailsTableLabels?.unshift('Manufacturer');
    var competitiveInsightsRadialData = competitiveInsightsTableData?.competitors?.slice(0,5).map((value, idx) => {
        return value.total_cases;
    });
    var competitiveInsightsDetailsRadialData = competitiveInsightsDetailsTableData?.competitors?.slice(0,5).map((value, idx) => {
        return value.total_cases;
    });
    competitiveInsightsRadialData?.unshift(competitiveInsightsTableData?.manufacturer[0].total_cases);
    competitiveInsightsDetailsRadialData?.unshift(competitiveInsightsDetailsTableData?.manufacturer[0].total_cases);
    const tableOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false,
          },
          legend: {
              display: false,
          },
          tooltip: {
              enabled: true,
              xAlign: 'center',
              yAlign: 'center'
          },
        },
    };
    competitiveInsightsRightTableData = competitiveInsightsTableLabels?.map((value, idx) => {
        return {"manufacturer" : value, "total_cases" : competitiveInsightsRadialData[idx]};
    });
    const competitiveInsightsDetailsRightLabels = competitiveInsightsDetailsTableLabels?.map((value, idx) => {
        return (
            <Row key={idx} className="my-2">
                <Col className="col-1">
                    <FaCircle className='w-fc' color={tableColors[idx % 12]}/>
                </Col>
                <Col>
                    <div className="w-fc p-0" style={{fontWeight: '600', fontSize: '14px'}}>{value}</div>
                </Col>
                <Col>
                    <div className="w-fc p-0" style={{fontWeight: '600', fontSize: '14px'}}>{numberFormatter.format(competitiveInsightsDetailsRadialData[idx])}</div>
                </Col>
            </Row>
        );
    });
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            );
        } else if (competitiveInsightsDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            );
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <Row className="mx-auto" style={{height: '60%', width: '100%'}}>
                        <Col style={{width: '100%', height: '100%'}}>
                            <Doughnut
                                data={{
                                    labels: competitiveInsightsDetailsTableLabels,
                                    datasets: [
                                        {
                                            label: '',
                                            data: competitiveInsightsDetailsRadialData,
                                            backgroundColor: tableColors,
                                        },
                                    ],
                                }}
                                options={tableOptions}
                            />
                        </Col>
                        <Col style={{width: '100%', height: '100%'}}>
                            {competitiveInsightsDetailsRightLabels}
                        </Col>
                    </Row>
                    <Row className="text-size-14 mx-0 px-0" style={{ height: '40%', width: '100%' }}>
                        <Col className="mx-0 px-0" style={{ height: '100%', width: '100%' }}>
                            <AutoSizer>
                                {({ width, height }) => (
                                    <Table
                                        className="table competitiveInsightsDetailsTable border-grayLight"
                                        id="competitiveInsightsDetailsTable"
                                        width={width}
                                        height={height}
                                        headerHeight={34.5}
                                        rowHeight={38}
                                        rowCount={competitiveInsightsDetailsTableData.competitors.length}
                                        rowGetter={({ index }) => competitiveInsightsDetailsTableData.competitors[index]}
                                    >
                                        <Column
                                            label="Manufacturer"
                                            dataKey="value"
                                            width={width * 0.9}
                                            cellRenderer={({ cellData, rowIndex }) => (
                                                <span>
                                                    <BsCircleFill color={tableColors[(rowIndex + 1) % 12]} /> Competitor {rowIndex + 1}
                                                </span>
                                            )}
                                        />
                                        <Column
                                            width={width * 0.2}
                                            label="Total Cases"
                                            dataKey="total_cases"
                                            cellRenderer={({ cellData }) => numberFormatter.format(cellData)}
                                        />
                                    </Table>
                                )}
                            </AutoSizer>
                        </Col>
                    </Row>
                </Col>
            );
        }
    };
    const detailsContent = () => {
        if (competitiveInsightsDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table competitiveInsightsDetailsTable border-grayLight" id="competitiveInsightsDetailsTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={competitiveInsightsDetailsTableData.competitors.length}
                                rowGetter={({ index }) => competitiveInsightsDetailsTableData.competitors[index]}
                            >
                                <Column
                                label='Manufacturer'
                                dataKey='value'
                                width={width * 0.8}
                                cellRenderer={({cellData, rowIndex}) => (<span><BsCircleFill color={tableColors[(rowIndex + 1) % 12]} /> Competitor {rowIndex + 1}</span>)}
                                />
                                <Column
                                width={width * 0.2}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>  
            )
        }
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle,
        tableOptions,
        competitiveInsightsRadialData,
        competitiveInsightsTableData,
        competitiveInsightsDetailsTableData,
        competitiveInsightsTableLabels,
        competitiveInsightsRightTableData
    };
} 

const useGrowthOpportunityWidget = () => {
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.growthOpportunityLoading);
    const { onClickViewItemMatches } = useDataSuite.useDataSuite();
    const growthOpportunityTableDataRaw = useSelector(({datasuite}) => datasuite.widgetData.growthOpportunityData);
    const growthOpportunityDetailsTableDataRaw = useSelector(({datasuite}) => datasuite.widgetDetailsData.growthOpportunityData);
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetTitle = 'Growth Opportunities';
    const [sortedDeprioritized, growthOpportunityTableData] = partition(growthOpportunityTableDataRaw, e => deprioritizedBrands.includes(e.location_name));
    const [sortedDetailsDeprioritized, growthOpportunityDetailsTableData] = partition(growthOpportunityDetailsTableDataRaw, e => deprioritizedBrands.includes(e.location_name));
    growthOpportunityDetailsTableData.sort((a,b) => b.opportunity_cases - a.opportunity_cases);
    growthOpportunityTableData.push(...sortedDeprioritized);
    growthOpportunityDetailsTableData.push(...sortedDetailsDeprioritized);
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(growthOpportunityDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <Row className='p-0 m-0' style={{height: '100%', width: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table growthOpportunitiesTable border-grayLight" id="growthOpportunitiesTable"
                                    width={width}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={growthOpportunityDetailsTableData.length}
                                    rowGetter={({ index }) => growthOpportunityDetailsTableData[index]}
                                >
                                    <Column
                                    label='Location Name'
                                    dataKey='location_name'
                                    width={width * 0.4}
                                    />
                                    <Column
                                    width={width * 0.3}
                                    label='Category'
                                    dataKey='category'
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Total Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    />
                                    <Column
                                    width={width * 0.1}
                                    label=''
                                    dataKey='total_cases'
                                    cellRenderer={({cellData, rowData}) => <span className='clickable' style={{color: Theme().colors.accent}} onClick={() => onClickViewDetails(widgetTitle, {locations: [rowData.location_name], categories: [rowData.category]})}>View Details</span>}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col>
            )
        }
    };
    const tableColumns = [
        {
            label: "Location Name",
            dataKey: "location_name",
            width: "0.25",
            formatter: "none",
            disableSort: 'true'
        }, 
        {
            label: "Category",
            dataKey: "category",
            width: "0.25",
            formatter: "none",
            disableSort: 'true'
        },
        {
            label: "Opportunity Cases",
            dataKey: "opportunity_cases",
            width: "0.2",
            formatter: "number",
            headerRenderer: 'true'
        },
        {
            label: "Total Cases",
            dataKey: "total_cases",
            width: "0.15",
            formatter: "number",
            disableSort: 'true'
        },
        {
            label: "",
            dataKey: "total_cases",
            width: "200",
            formatter: "none",
            disableSort: 'true',
            cellRenderer: ({cellData, rowData}) => <span className='clickable' style={{color: Theme().colors.accent}} onClick={() => onClickViewItemMatches(rowData.location_name, rowData.category)}>View Your Item Matches</span>
        }
    ]; 
    const detailsContent = () => {
        if(growthOpportunityDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="px-0">
                    <CollapsibleDataTable list={growthOpportunityDetailsTableData} tableColumns={tableColumns} tableName={"growthOpportunitiesTable"} widgetTitle={widgetTitle} />
                </Col>
            )
        }
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle,
        growthOpportunityTableData,
        growthOpportunityDetailsTableData
    };
}

const useLeakageWidget = () => {
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.leakageLoading);
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetTitle = 'Leakage';
    const leakageTableDataRaw = useSelector(({datasuite}) => datasuite.widgetData.leakageData);
    const leakageDetailsTableDataRaw = useSelector(({datasuite}) => datasuite.widgetDetailsData.leakageData);
    const sortedTableData = leakageTableDataRaw?.sort((a, b) => b.total_cases - a.total_cases);
    const sortedDetailsTableData = leakageDetailsTableDataRaw?.sort((a, b) => b.total_cases - a.total_cases);
    const [sortedDeprioritized, leakageTableData] = partition(sortedTableData, e => deprioritizedBrands.includes(e.location_name));
    const [sortedDetailsDeprioritized, leakageDetailsTableData] = partition(sortedDetailsTableData, e => deprioritizedBrands.includes(e.location_name));
    leakageTableData.push(...sortedDeprioritized);
    leakageDetailsTableData.push(...sortedDetailsDeprioritized);
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(leakageDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table leakageTable border-grayLight" id="leakageTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={sortedDetailsTableData.length}
                                rowGetter={({ index }) => sortedDetailsTableData[index]}
                            >
                                <Column
                                label='Location Name'
                                dataKey='location_name'
                                width={width * 0.25}
                                />
                                <Column
                                width={width * 0.2}
                                label='Category'
                                dataKey='category'
                                />
                                <Column
                                width={width * 0.15}
                                label='Your Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                                <Column
                                width={width * 0.2}
                                label='Competitor Cases'
                                dataKey='total_cases_comp'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                                <Column
                                width={width * 0.15}
                                label=''
                                dataKey='total_cases'
                                cellRenderer={({cellData, rowData}) => <span className='clickable' style={{color: Theme().colors.accent}} onClick={() => onClickViewDetails(widgetTitle, {locations: [rowData.location_name], categories: [rowData.category]})}>View Details</span>}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            )
        }
    };
    const tableColumns = [
        {
            label: "Location Name",
            dataKey: "location_name",
            width: "0.4",
            formatter: "none",
            disableSort: 'true'
        }, 
        {
            label: "Category",
            dataKey: "category",
            width: "0.3",
            formatter: "none",
            disableSort: 'true'
        },
        {
            label: "Your Cases",
            dataKey: 'total_cases',
            width: '0.1',
            formatter: 'number',
            disableSort: 'true'
        },
        {
            label: "Opportunity Cases",
            dataKey: 'total_cases_comp',
            width: "0.2",
            formatter: "number",
            headerRenderer: 'true'
        }
    ];  
    const detailsContent = () => {
        if(leakageDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="px-0">
                    <CollapsibleDataTable list={leakageDetailsTableData} tableColumns={tableColumns} tableName={"leakageTable"} widgetTitle={widgetTitle} />
                </Col>
            )
        }
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle,
        leakageTableData,
        leakageDetailsTableData
    };
}

const useLostLocationsWidget = () => {
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.lostLocationsLoading);
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetTitle = 'Lost Locations';
    const lostLocationsTableData = useSelector(({datasuite}) => datasuite.widgetData.lostLocationsData);
    const lostLocationsDetailsTableData = useSelector(({datasuite}) => datasuite.widgetDetailsData.lostLocationsData);
    const sortedTableData = lostLocationsTableData?.sort((a, b) => b.total_cases - a.total_cases);
    const sortedDetailsTableData = lostLocationsDetailsTableData?.sort((a, b) => b.total_cases - a.total_cases);
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(sortedDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table lostLocationsTable border-grayLight" id="lostLocationsTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={sortedDetailsTableData.length}
                                rowGetter={({ index }) => sortedDetailsTableData[index]}
                            >
                                <Column
                                label='Location'
                                dataKey='location_name'
                                width={width * 0.6}
                                />
                                <Column
                                width={width * 0.2}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                                <Column
                                width={width * 0.15}
                                label=''
                                dataKey='total_cases'
                                cellRenderer={({cellData, rowData}) => <span className='clickable' style={{color: Theme().colors.accent}} onClick={() => onClickViewDetails(widgetTitle, {locations: [rowData.location_name]})}>View Details</span>}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            )
            
        } 
    };
    const detailsContent = () => {
        if(sortedDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table lostLocationsTable border-grayLight" id="lostLocationsTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={sortedDetailsTableData.length}
                                rowGetter={({ index }) => sortedDetailsTableData[index]}
                            >
                                <Column
                                label='Location'
                                dataKey='location_name'
                                width={width * 0.8}
                                />
                                <Column
                                width={width * 0.2}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col> 
            )
        }
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle,
        lostLocationsTableData,
        sortedTableData,
        sortedDetailsTableData
    };
}

const useNewLocationsWidget = () => {
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.newLocationsLoading);
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetTitle = 'New Locations';
    const newLocationsTableData = useSelector(({datasuite}) => datasuite.widgetData.newLocationsData);
    const newLocationsDetailsTableData = useSelector(({datasuite}) => datasuite.widgetDetailsData.newLocationsData);
    const sortedTableData = newLocationsTableData?.sort((a, b) => b.total_cases - a.total_cases);
    const sortedDetailsTableData = newLocationsDetailsTableData?.sort((a, b) => b.total_cases - a.total_cases);
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(sortedDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table newLocationsTable border-grayLight" id="newLocationsTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={sortedDetailsTableData.length}
                                rowGetter={({ index }) => sortedDetailsTableData[index]}
                            >
                                <Column
                                label='Location'
                                dataKey='location_name'
                                width={width * 0.65}
                                />
                                <Column
                                width={width * 0.2}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                                <Column
                                width={width * 0.15}
                                label=''
                                dataKey='total_cases'
                                cellRenderer={({cellData, rowData}) => <span className='clickable' style={{color: Theme().colors.accent}} onClick={() => onClickViewDetails(widgetTitle, {locations: [rowData.location_name]})}>View Details</span>}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            )
        }
    };
    const tableColumns = [
        {
            label: "Location Name",
            dataKey: "location_name",
            width: "0.5",
            formatter: "none",
            disableSort: 'true'
        }, 
        {
            label: "Total Cases",
            dataKey: "total_cases",
            width: "0.5",
            formatter: "number",
            disableSort: 'true'
        }
    ]; 

    const detailsContent = () => {
        if(sortedDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="px-0">
                    <CollapsibleDataTable list={sortedDetailsTableData} tableColumns={tableColumns} tableName={"newLocationsTable"} widgetTitle={widgetTitle} />
                </Col>
            )
        }  
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle,
        newLocationsTableData,
        sortedTableData,
        sortedDetailsTableData
    };
}

const useOpportunityByStoreWidget = () => {
    const widgetTitle = 'Opportunities By Store';
    const opportunityByStoreTableDataRaw = useSelector(({datasuite}) => datasuite.widgetData.opportunityByStoreData);
    const opportunityByStoreDetailsTableDataRaw = useSelector(({datasuite}) => datasuite.widgetDetailsData.opportunityByStoreData);
    const sortedTableData = opportunityByStoreTableDataRaw?.sort((a, b) => b.total_cases - a.total_cases);
    const sortedDetailsTableData = opportunityByStoreDetailsTableDataRaw?.sort((a, b) => b.total_cases - a.total_cases);
    const [sortedDeprioritized, opportunityByStoreTableData] = partition(sortedTableData, e => deprioritizedBrands.includes(e.location_name));
    const [sortedDetailsDeprioritized, opportunityByStoreDetailsTableData] = partition(sortedDetailsTableData, e => deprioritizedBrands.includes(e.location_name));
    opportunityByStoreTableData.push(...sortedDeprioritized);
    opportunityByStoreDetailsTableData.push(...sortedDetailsDeprioritized);
    const opportunityByStoreTableLabels = opportunityByStoreTableData?.slice(0,9).map((value, idx) => {
        return value.location_name;
    });
    const opportunityByStoreBarData = opportunityByStoreTableData?.slice(0,9).map((value, idx) => {
        return value.total_cases;
    });
    const tableOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display:false
                },
                ticks: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Total Cases'
                }
            },
            y: {
                grid: {
                    display: false
                }
            }
        },
        plugins: {
          title: {
            display: false,
          },
          legend: {
              display: false,
          },
          tooltip: {
              enabled: true
          },
        },
    };
    const detailsContent = () => {
        if(opportunityByStoreDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table opportunityByStoreDetailsTable border-grayLight" id="opportunityByStoreDetailsTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={opportunityByStoreDetailsTableData.length}
                                rowGetter={({ index }) => opportunityByStoreDetailsTableData[index]}
                            >
                                <Column
                                label='Location Name'
                                dataKey='location_name'
                                width={width * 0.8}
                                cellRenderer={({cellData, rowIndex}) => (<span><BsCircleFill color={tableColors[rowIndex % 12]} /> {cellData}</span>)}
                                />
                                <Column
                                width={width * 0.2}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            )
        } 
    };
    return {
        detailsContent,
        widgetTitle,
        tableOptions,
        opportunityByStoreBarData,
        opportunityByStoreTableLabels,
        opportunityByStoreDetailsTableData
    };
}

const useSalesByCategoryWidget = () => {
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.salesByCategoryLoading);
    const salesByCategoryTableData = useSelector(({datasuite}) => datasuite.widgetData.salesByCategoryData);
    const salesByCategoryDetailsTableData = useSelector(({datasuite}) => datasuite.widgetDetailsData.salesByCategoryData);
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetTitle = 'Sales By Category';
    const sortedTableData = salesByCategoryTableData?.sort((a, b) => b.total_cases - a.total_cases);
    const sortedDetailsTableData = salesByCategoryDetailsTableData?.sort((a, b) => b.total_cases - a.total_cases);
    const salesByCategoryTableLabels = sortedTableData?.slice(0,9).map((value, idx) => {
        return value.category;
    });
    const salesByCategoryBarData = sortedTableData?.slice(0,9).map((value, idx) => {
        return value.total_cases;
    });
    const salesByCategoryDetailsTableLabels = sortedDetailsTableData?.slice(0,19).map((value, idx) => {
        return value.category;
    });
    const salesByCategoryDetailsBarData = sortedDetailsTableData?.slice(0,19).map((value, idx) => {
        return value.total_cases;
    });
    const tableOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display:false
                },
                ticks: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Total Cases'
                }
            },
            y: {
                grid: {
                    display: false
                }
            }
        },
        plugins: {
          title: {
            display: false,
          },
          legend: {
              display: false,
          },
          tooltip: {
              enabled: true
          },
        },
    };
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(sortedDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0" >
                    <Row className="mx-auto" style={{height: '50%', width: '100%'}}>
                        <Bar
                            data = {{
                                labels: salesByCategoryDetailsTableLabels, 
                                datasets: [
                                    {
                                        label: '',
                                        data: salesByCategoryDetailsBarData,
                                        backgroundColor: tableColors
                                    }
                                ]
                            }}
                            options = {tableOptions}
                        />
                    </Row>
                    <Row className="text-size-14 px-0 mx-0" style={{height: '50%', width: '100%'}}>
                        <Col className='p-0 m-0' style={{height: '100%', width: '100%'}}>
                            <AutoSizer>
                                {({width, height}) => (
                                    <Table className="table salesByCategoryDetailsTable border-grayLight" id="salesByCategoryDetailsTable"
                                        width={width}
                                        height={height}
                                        headerHeight={34.5}
                                        rowHeight={38}
                                        rowCount={sortedDetailsTableData.length}
                                        rowGetter={({ index }) => sortedDetailsTableData[index]}
                                    >
                                        <Column
                                        label='Category'
                                        dataKey='category'
                                        width={width * 0.6}
                                        cellRenderer={({cellData, rowIndex}) => (<span><BsCircleFill color={tableColors[rowIndex % 12]} /> {cellData}</span>)}
                                        />
                                        <Column
                                        width={width * 0.2}
                                        label='Total Cases'
                                        dataKey='total_cases'
                                        cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                        />
                                        <Column
                                        width={width * 0.15}
                                        label=''
                                        dataKey='total_cases'
                                        cellRenderer={({cellData, rowData}) => <span className='clickable' style={{color: Theme().colors.accent}} onClick={() => onClickViewDetails(widgetTitle, {categories: [rowData.category]})}>View Details</span>}
                                        />
                                    </Table>
                                )}
                            </AutoSizer>
                        </Col>
                    </Row>
                </Col>
            )
        }
    };
    const tableColumns = [
        {
            label: "Category",
            dataKey: "category",
            width: "0.5",
            formatter: "none",
            disableSort: 'true'
        },
        {
            label: "Total Cases",
            dataKey: "total_cases",
            width: "0.5",
            formatter: "number",
            disableSort: 'true'
        }
    ]; 
    const detailsContent = () => {
        if(sortedDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="px-0">
                    <CollapsibleDataTable list={sortedDetailsTableData} tableColumns={tableColumns} tableName={"salesByCategoryDetailsTable"} widgetTitle={widgetTitle} />
                </Col>
            )
        }
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle,
        tableOptions,
        salesByCategoryBarData,
        salesByCategoryTableLabels,
        sortedDetailsTableData
    };
}

const useSalesByOperatorWidget = () => {
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.salesByOperatorLoading);
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetTitle = 'Sales By Operator';
    const salesByOperatorTableDataRaw = useSelector(({datasuite}) => datasuite.widgetData.salesByOperatorData);
    const salesByOperatorDetailsTableDataRaw = useSelector(({datasuite}) => datasuite.widgetDetailsData.salesByOperatorData);
    const sortedTableData = salesByOperatorTableDataRaw?.sort((a, b) => b.total_cases - a.total_cases);
    const sortedDetailsTableData = salesByOperatorDetailsTableDataRaw?.sort((a, b) => b.total_cases - a.total_cases);
    const [sortedDeprioritized, salesByOperatorTableData] = partition(sortedTableData, e => deprioritizedBrands.includes(e.location_name) || deprioritizedBrands.includes(e.group_name));
    const [sortedDetailsDeprioritized, salesByOperatorDetailsTableData] = partition(sortedDetailsTableData, e => deprioritizedBrands.includes(e.location_name) || deprioritizedBrands.includes(e.group_name));
    salesByOperatorTableData.push(...sortedDeprioritized);
    salesByOperatorDetailsTableData.push(...sortedDetailsDeprioritized);
    const salesByOperatorTableLabels = salesByOperatorTableData?.slice(0,10).map((value, idx) => {
        return value.location_name;
    });
    const salesByOperatorBarData = salesByOperatorTableData?.slice(0,10).map((value, idx) => {
        return value.total_cases;
    });
    const salesByOperatorDetailsTableLabels = salesByOperatorDetailsTableData?.slice(0,20).map((value, idx) => {
        return value.location_name;
    });
    const salesByOperatorDetailsBarData = salesByOperatorDetailsTableData?.slice(0,20).map((value, idx) => {
        return value.total_cases;
    });
    const tableOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display:false
                },
                ticks: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Case Quantity'
                }
            },
            y: {
                grid: {
                    display: false
                }
            }
        },
        plugins: {
          title: {
            display: false,
          },
          legend: {
              display: false
          },
          tooltip: {
              enabled: true
          },
        },
    };
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(salesByOperatorDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <Row className="px-2" style={{height: '50%', width: '100%'}}>
                        <Bar
                            data = {{
                                labels: salesByOperatorDetailsTableLabels, 
                                datasets: [
                                    {
                                        label: '',
                                        data: salesByOperatorDetailsBarData,
                                        backgroundColor: tableColors
                                    }
                                ]
                            }}
                            options = {tableOptions}
                        />
                    </Row>
                    <Row style={{height: '50%', width: '100%'}}>
                        <Col style={{height: '100%', width: '100%'}}>
                            <AutoSizer>
                                {({width, height}) => (
                                    <Table className="table salesByOperatorTable border-grayLight" id="salesByOperatorDetailsTable"
                                        width={width}
                                        height={height}
                                        headerHeight={34.5}
                                        rowHeight={38}
                                        rowCount={salesByOperatorDetailsTableData.length}
                                        rowGetter={({ index }) => salesByOperatorDetailsTableData[index]}
                                    >
                                        <Column
                                        label='Location Name'
                                        dataKey='location_name'
                                        width={width * 0.4}
                                        cellRenderer={({cellData, rowIndex}) => (<span><BsCircleFill color={tableColors[rowIndex % 12]} /> {cellData}</span>)}
                                        />
                                        <Column
                                        width={width * 0.25}
                                        label='Category'
                                        dataKey='category'
                                        />
                                        <Column
                                        width={width * 0.2}
                                        label='Total Cases'
                                        dataKey='total_cases'
                                        cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                        />
                                        <Column
                                        width={width * 0.15}
                                        label=''
                                        dataKey='total_cases'
                                        cellRenderer={({cellData, rowData}) => <span className='clickable' style={{color: Theme().colors.accent}} onClick={() => onClickViewDetails(widgetTitle, {locations: [rowData.location_name]})}>View Details</span>}
                                        />
                                    </Table>
                                )}
                            </AutoSizer>
                        </Col>
                    </Row>
                </Col>
            )
        }
    };
    const tableColumns = [
        {
            label: "Location Name",
            dataKey: "location_name",
            width: "0.4",
            formatter: "none",
            disableSort: 'true'
        }, 
        {
            label: "State",
            dataKey: "state",
            width: "0.1",
            formatter: "none",
            disableSort: 'true'
        },
        {
            label: "Category",
            dataKey: "category",
            width: "0.4",
            formatter: "none",
            disableSort: 'true'
        },
        {
            label: "Total Cases",
            dataKey: "total_cases",
            width: "0.1",
            formatter: "number",
            disableSort: 'true'
        }
    ]; 
    const detailsContent = () => {
        if(salesByOperatorDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="px-0">
                    <CollapsibleDataTable list={salesByOperatorDetailsTableData} tableColumns={tableColumns} tableName={"salesByOperatorDetailsTable"} widgetTitle={widgetTitle} />
                </Col>
            )
        }  
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle,
        salesByOperatorTableData,
        salesByOperatorTableLabels,
        salesByOperatorBarData,
        tableOptions,
        salesByOperatorDetailsTableData
    };
}

const useSalesVoidOpportunityWidget = () => {
    const widgetTitle = 'Sales Void Opportunities';
    const salesVoidOpportunityTableDataRaw = useSelector(({datasuite}) => datasuite.widgetData.salesVoidData);
    const salesVoidOpportunityDetailsTableDataRaw = useSelector(({datasuite}) => datasuite.widgetDetailsData.salesVoidData);
    const sortedTableData = salesVoidOpportunityTableDataRaw?.sort((a, b) => a.total_cases - b.total_cases);
    const sortedDetailsTableData = salesVoidOpportunityDetailsTableDataRaw?.sort((a, b) => a.total_cases - b.total_cases);
    const [sortedDeprioritized, salesVoidOpportunityTableData] = partition(sortedTableData, e => deprioritizedBrands.includes(e.location_name));
    const [sortedDetailsDeprioritized, salesVoidOpportunityDetailsTableData] = partition(sortedDetailsTableData, e => deprioritizedBrands.includes(e.location_name));
    salesVoidOpportunityTableData.push(...sortedDeprioritized);
    salesVoidOpportunityDetailsTableData.push(...sortedDetailsDeprioritized);
    const tableColumns = [
        {
            label: "Location Name",
            dataKey: "location_name",
            width: "0.3",
            formatter: "none",
            disableSort: 'true'
        }, 
        {
            label: "Your Cases",
            dataKey: "total_cases",
            width: "0.2",
            formatter: "number",
            disableSort: 'true'
        },
        {
            label: "Opportunity Cases",
            dataKey: "total_cases_comp",
            width: "0.2",
            formatter: "number",
            headerRenderer: 'true'
        },
        {
            label: "Your %",
            dataKey: "mfr_percent",
            width: "0.15",
            formatter: "percentage",
            disableSort: 'true'
        },
        {
            label: "% of Competitors",
            dataKey: "comp_percent",
            width: "0.15",
            formatter: "percentage",
            disableSort: 'true'
        }
    ]; 
    const detailsContent = () => {
        if(salesVoidOpportunityDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="px-0">
                    <CollapsibleDataTable list={salesVoidOpportunityDetailsTableData} tableColumns={tableColumns} tableName={"salesVoidOpportunityTable"} widgetTitle={widgetTitle} />
                </Col> 
            )
        }
    };
    return {
        detailsContent,
        widgetTitle, 
        salesVoidOpportunityTableData,
        salesVoidOpportunityDetailsTableData
    };
}

const useTargetsByCategoryWidget = () => {
    const widgetTitle = 'Targets By Category';
    const targetsByCategoryTableData = useSelector(({datasuite}) => datasuite.widgetData.targetsByCategoryData);
    const targetsByCategoryDetailsTableData = useSelector(({datasuite}) => datasuite.widgetDetailsData.targetsByCategoryData);
    const targetsByCategoryMap = targetsByCategoryTableData?.map((data, idx) =>{
        return (
            <tr className='tr' key={`${idx}`}>
                <td className='td'>{data?.category}</td>
                <td className='td'>{numberFormatter.format(data?.total_cases)}</td>
                <td className='td'>{numberFormatter.format(data?.total_cases_comp)}</td>
            </tr>
        )
    });
    const tableColumns = [
        {
            label: "Category",
            dataKey: "category",
            width: "0.4",
            formatter: "none",
            disableSort: 'true'
        }, 
        {
            label: "Your Cases",
            dataKey: "total_cases",
            width: "0.3",
            formatter: "number",
            disableSort: 'true'
        },
        {
            label: "Opportunity Cases",
            dataKey: "total_cases_comp",
            width: "0.3",
            formatter: "number",
            headerRenderer: 'true'
        },
    ]; 
    const detailsContent = () => {
        if(targetsByCategoryDetailsTableData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="px-0">
                    <CollapsibleDataTable list={targetsByCategoryDetailsTableData} tableColumns={tableColumns} tableName={"targetsByCategoryTable"} widgetTitle={widgetTitle} />
                </Col> 
            )
        }
    };

    return {
        detailsContent,
        widgetTitle,
        targetsByCategoryTableData,
        targetsByCategoryDetailsTableData,
        targetsByCategoryMap
    };
}

const useTopProductsWidget = () => {
    const topProductsDetailsData = useSelector(({datasuite}) => datasuite.widgetDetailsData.topProductsData);
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.topProductsLoading);
    const widgetTitle = 'Top Products';
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(topProductsDetailsData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <Row style={{height: '100%', width: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table topProductsTable border-grayLight" id="topProductsTable"
                                    width={width}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={topProductsDetailsData?.length}
                                    rowGetter={({ index }) => topProductsDetailsData[index]}
                                >
                                    <Column
                                    label='MIN'
                                    dataKey='min'
                                    width={width * 0.4}
                                    />
                                    <Column
                                    width={width * 0.4}
                                    label='Description'
                                    dataKey='description'
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Total Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='% of Sales'
                                    dataKey='sales_percent'
                                    cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col> 
            )
        }
    };
    const detailsContent = () => {
        if(topProductsDetailsData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table topProductsTable border-grayLight" id="topProductsTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={topProductsDetailsData?.length}
                                rowGetter={({ index }) => topProductsDetailsData[index]}
                            >
                                <Column
                                label='MIN'
                                dataKey='min'
                                width={width * 0.4}
                                />
                                <Column
                                width={width * 0.4}
                                label='Description'
                                dataKey='description'
                                />
                                <Column
                                width={width * 0.2}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                                <Column
                                width={width * 0.2}
                                label='% of Sales'
                                dataKey='sales_percent'
                                cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col> 
            )
        }
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle
    };
}

const useTopCategoriesWidget = () => {
    const topCategoriesDetailsData = useSelector(({datasuite}) => datasuite.widgetDetailsData.topCategoriesData);
    const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.topCategoriesLoading);
    const widgetTitle = 'Top Categories';
    const widgetDetailsContent = () => {
        if (isLoading) {
            return (
                <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
            )
        } else if(topCategoriesDetailsData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <Row style={{height: '100%', width: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table topCategoriesTable border-grayLight" id="topCategoriesTable"
                                    width={width}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={topCategoriesDetailsData?.length}
                                    rowGetter={({ index }) => topCategoriesDetailsData[index]}
                                >
                                    <Column
                                    label='Category'
                                    dataKey='category'
                                    width={width * 0.4}
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Total Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='% of Sales'
                                    dataKey='sales_percent'
                                    cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col> 
            )
        }
    };
    const detailsContent = () => {
        if(topCategoriesDetailsData.length <= 0) {
            return (
                <EmptySearchResultsMessage />
            )
        } else {
            return (
                <Col className="h-100 w-100 px-0">
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table topCategoriesTable border-grayLight" id="topCategoriesTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={topCategoriesDetailsData?.length}
                                rowGetter={({ index }) => topCategoriesDetailsData[index]}
                            >
                                <Column
                                label='Category'
                                dataKey='category'
                                width={width * 0.4}
                                />
                                <Column
                                width={width * 0.2}
                                label='Total Cases'
                                dataKey='total_cases'
                                cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                />
                                <Column
                                width={width * 0.2}
                                label='% of Sales'
                                dataKey='sales_percent'
                                cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col> 
            )
        }
    };
    return {
        widgetDetailsContent,
        detailsContent,
        widgetTitle
    };
}

const useSegmentOverviewWidget = () => {
  const segmentOverviewDetailsData = useSelector(({datasuite}) => datasuite.widgetDetailsData.segmentOverviewData);
  const isLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.segmentOverviewLoading);
  const widgetTitle = "Segment Overview";
  const { numberFormatter, percentFormatter } = formatter();

  const widgetDetailsContent = () => {
    if (isLoading) {
      return (
        <Spinner style={{marginLeft:'50%', marginTop: '30%'}} color='beplBlue' animation="border" />
      );
    } else if (segmentOverviewDetailsData.length <= 0) {
      return <EmptySearchResultsMessage />;
    } else {
      return (
        <Col className="h-100 w-100 px-0">
          <Row style={{height: '100%', width: '100%'}}>
            <AutoSizer>
              {({width, height}) => (
                <Table
                  className="table segmentOverviewTable border-grayLight"
                  id="segmentOverviewDetailsTable"
                  width={width}
                  height={height}
                  headerHeight={34.5}
                  rowHeight={38}
                  rowCount={segmentOverviewDetailsData?.length}
                  rowGetter={({ index }) => segmentOverviewDetailsData[index]}
                >
                  <Column label='Segment' dataKey='segment' width={width * 0.4} />
                  <Column 
                    label='Total Cases'
                    dataKey='total_cases'
                    width={width * 0.2}
                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                  />
                  <Column 
                    label='Total Volume'
                    dataKey='total_volume'
                    width={width * 0.2}
                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                  />
                  <Column 
                    label='% of Sales'
                    dataKey='sales_percent'
                    width={width * 0.2}
                    cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                  />
                </Table>
              )}
            </AutoSizer>
          </Row>
        </Col>
      );
    }
  };

  const detailsContent = () => {
    if (segmentOverviewDetailsData.length <= 0) {
      return <EmptySearchResultsMessage />;
    } else {
      return (
        <Col className="h-100 w-100 px-0">
            <AutoSizer>
              {({width, height}) => (
                <Table
                  className="table segmentOverviewDetailsTable border-grayLight"
                  id="segmentOverviewDetailsTable"
                  width={width}
                  height={height}
                  headerHeight={34.5}
                  rowHeight={38}
                  rowCount={segmentOverviewDetailsData?.length}
                  rowGetter={({ index }) => segmentOverviewDetailsData[index]}
                >
                  <Column 
                    label='Segment'
                    dataKey='segment'
                    width={width * 0.4}
                  />
                  <Column 
                    label='Total Cases'
                    dataKey='total_cases'
                    width={width * 0.2}
                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                  />
                  <Column 
                    label='Total Volume'
                    dataKey='total_volume'
                    width={width * 0.2}
                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                  />
                  <Column 
                    label='% of Sales'
                    dataKey='sales_percent'
                    width={width * 0.2}
                    cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                  />
                </Table>
              )}
            </AutoSizer>
        </Col>
      );
    }
  };

  return {
    widgetDetailsContent,
    detailsContent,
    widgetTitle
  };
};

// eslint-disable-next-line
export default { useCompetitiveInsightsWidget, useGrowthOpportunityWidget, useLeakageWidget, useLostLocationsWidget, useNewLocationsWidget, useOpportunityByStoreWidget, useSalesByCategoryWidget,
                 useSalesByOperatorWidget, useSalesVoidOpportunityWidget, useTargetsByCategoryWidget, useTopProductsWidget, useTopCategoriesWidget, useSegmentOverviewWidget };